import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { offerApi } from '@/api';
import { Button, Icon, LinkButton, Modal, Textarea, useToast } from '@/components/ui';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';

/**
 * Accept or decline the open offer on one application, where the student is
 * already reading about it -- the same answer, dialog and message as the
 * Offers page, so the two never disagree about what answering means.
 *
 * The student's offers endpoint has no application filter, so the open ones
 * are fetched and matched here. If none matches (it was withdrawn a moment
 * ago, say) the Offers page is offered instead.
 */
export function AnswerOfferActions({ applicationId }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [accepting, setAccepting] = useState(null);
  const [message, setMessage] = useState('');

  /** Opens the answer dialog with an empty message: text typed for a
   * decline and then backed out of must not reappear on an accept. */
  function openAnswer(accept) {
    setMessage('');
    setAccepting(accept);
  }

  const { data, isPending } = useQuery({
    queryKey: ['student-offers', { statuses: 'SENT', page_size: 50 }],
    queryFn: () => offerApi.listOffers({ statuses: 'SENT', page: 1, page_size: 50 }),
  });
  const offer = (data?.data ?? []).find(
    (row) => row.application_id === applicationId && row.can_respond,
  );

  const answer = useMutation({
    mutationFn: ({ accept, note }) =>
      accept ? offerApi.acceptOffer(offer.id, note) : offerApi.declineOffer(offer.id, note),
    onSuccess: (result) => {
      toast.success(
        result.status === 'ACCEPTED'
          ? 'Accepted. Congratulations!'
          : 'Declined. The company has been told.',
      );
      void queryClient.invalidateQueries({ queryKey: ['student-offers'] });
      void queryClient.invalidateQueries({ queryKey: ['student-application', applicationId] });
      void queryClient.invalidateQueries({ queryKey: ['student-applications'] });
      void queryClient.invalidateQueries({ queryKey: ['student-dashboard'] });
      setAccepting(null);
      setMessage('');
    },
    onError: (answerError) => toast.error(errorMessage(answerError, 'Could not send your answer')),
  });

  const letter = useMutation({
    mutationFn: () => offerApi.downloadOfferLetter(offer.id),
    onError: (letterError) =>
      toast.error(errorMessage(letterError, 'Could not download the offer letter')),
  });

  if (isPending) return null;
  if (!offer) {
    return (
      <LinkButton size="sm" to={paths.student.offers}>
        See your offers
      </LinkButton>
    );
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          title="Offer letter (PDF)"
          isLoading={letter.isPending}
          onClick={() => letter.mutate()}
        >
          <Icon name="download" size={14} />
          Offer letter
        </Button>
        <Button variant="tintDanger" size="sm" onClick={() => openAnswer(false)}>
          <Icon name="close" size={14} />
          Decline
        </Button>
        <Button variant="tintPositive" size="sm" onClick={() => openAnswer(true)}>
          <Icon name="check" size={14} />
          Accept
        </Button>
      </div>

      <Modal
        open={accepting !== null}
        title={
          accepting
            ? `Accept the offer from ${offer.company_name}?`
            : `Decline the offer from ${offer.company_name}?`
        }
        description={
          accepting
            ? 'The company then marks you as joined on your first day. Accepting cannot be undone here.'
            : 'Declining also takes you out of that company’s pipeline.'
        }
        onClose={() => setAccepting(null)}
      >
        <div className="space-y-4">
          <Textarea
            label="Message"
            rows={3}
            hint="Optional. The company reads this."
            placeholder={
              accepting
                ? 'Delighted — see you in September.'
                : 'Thank you, but I have accepted somewhere else.'
            }
            value={message}
            onChange={(event) => setMessage(event.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setAccepting(null)}>
              Back
            </Button>
            <Button
              variant={accepting ? 'primary' : 'danger'}
              isLoading={answer.isPending}
              onClick={() => answer.mutate({ accept: accepting, note: message.trim() || null })}
            >
              {accepting ? 'Accept offer' : 'Decline offer'}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
